import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { TrackerActivationType } from '@racemap/utilities/consts/trackers';
import { Badge, Dropdown, type MenuProps } from 'antd';
import type { FC } from 'react';
import { useIsAdmin, usePriceList, useTrackersForAction } from '../../../lib/customHooks';
import {
  IconDownload,
  IconEmail,
  IconErase,
  IconEuro,
  IconGear,
  IconGlobe,
  IconInStock,
  IconPlus,
  IconPowerOff,
  IconRefresh,
  IconSIM,
  IconShare,
  IconTrash,
  IconUser,
} from '../../Icon';
import { ActivateSimCardsButton, isAvailableSimCard } from './ActivateSimCardsButton';
import { AddTrackerToEventButton } from './AddTrackerToEventButton';
import { CSVDownloadButton } from './CSVDownloadButton';
import { ChangeActivationStateButton } from './ChangeActivationStateButton';
import { ChangeLogisticsButton } from './ChangeLogisticsButton';
import { ClearErrorMessagesButton } from './ClearErrorMessagesButton';
import { ManageTrackerUsers } from './ManageTrackerUsers';
import { RemoveTrackerButton } from './RemoveTrackerButton';
import { RevokeAllMessagesButton } from './RevokeAllMessagesButton';
import { SendMessagesToTrackerButton } from './SendMessagesToTrackerButton';
import { ShareTrackerButton } from './ShareTrackerButton';
import { TerminateSimCardsButton, isActivSimCard } from './TerminateSimCardsButton';

export const ActionsDropDown: FC = () => {
  const trackersToUse = useTrackersForAction();
  const isAdmin = useIsAdmin();
  const { priceList, isLoading } = usePriceList();

  if (trackersToUse == null) return <></>;

  const archivedTrackers = trackersToUse.filter(
    (t) => t.activation.type === TrackerActivationType.ARCHIVED,
  );
  const noAdminWithArchivedInSample =
    archivedTrackers != null && archivedTrackers?.length > 0 && !isAdmin;
  const disabled = trackersToUse.length === 0;
  const trackersWithAvailableSims = trackersToUse.filter(isAvailableSimCard);
  const trackersWithActivSims = trackersToUse.filter(isActivSimCard);

  const items: MenuProps['items'] = [
    {
      key: 'trackers-info',
      label: noAdminWithArchivedInSample ? (
        <TrackersInfo
          title={
            'You have archived trackers in your selection. To enable the batch actions restore the archived trackers or remove them from the selection.'
          }
        >
          <b>{archivedTrackers != null ? archivedTrackers.length : '0'}</b> archived trackers in
          selection
        </TrackersInfo>
      ) : (
        <TrackersInfo>
          <b>{trackersToUse != null ? trackersToUse.length : '0'}</b> trackers in selection
        </TrackersInfo>
      ),
      disabled: true,
    },
    { type: 'divider' as const },
    {
      key: 'clear-error-messages',
      label: <ClearErrorMessagesButton />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconErase flip="horizontal" fixedWidth />,
    },
    {
      key: 'revoke-all-messages',
      label: <RevokeAllMessagesButton />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconRefresh flip="horizontal" fixedWidth />,
    },
    {
      key: 'send-messages-to-tracker',
      label: <SendMessagesToTrackerButton trackersToUse={trackersToUse} />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconEmail fixedWidth />,
    },
    {
      key: 'add-tracker-to-event',
      label: <AddTrackerToEventButton />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconPlus fixedWidth />,
    },
    {
      key: 'change-activation-type',
      label: <ChangeActivationStateButton />,
      icon: <IconEuro fixedWidth />,
    },
    {
      key: 'manage-user',
      label: <ManageTrackerUsers />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconUser fixedWidth />,
    },
    {
      key: 'change-logistics',
      label: <ChangeLogisticsButton />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconInStock fixedWidth />,
    },
    {
      key: 'csv-download',
      label: <CSVDownloadButton />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconDownload fixedWidth />,
    },
    {
      key: 'share-trackers',
      label: <ShareTrackerButton />,
      disabled: noAdminWithArchivedInSample,
      icon: <IconShare fixedWidth />,
    },
    {
      key: 'sim-cards',
      label: 'SIM-Cards',
      disabled: noAdminWithArchivedInSample,
      icon: <IconSIM fixedWidth />,
      children: [
        {
          key: 'sim-cards-activate',
          label: <ActivateSimCardsButton priceList={priceList} />,
          icon: <IconGlobe fixedWidth />,
          disabled: isLoading || trackersWithAvailableSims.length === 0,
        },
        {
          key: 'sim-cards-terminate',
          label: <TerminateSimCardsButton />,
          icon: <IconPowerOff fixedWidth />,
          disabled: isLoading || trackersWithActivSims.length === 0,
        },
      ],
    },
    isAdmin && {
      key: 'remove-trackers',
      label: <RemoveTrackerButton />,
      icon: <IconTrash fixedWidth />,
    },
  ].filter((item): item is Exclude<typeof item, false> => Boolean(item));

  return (
    <Dropdown
      menu={{
        items,
      }}
      disabled={disabled}
      trigger={['click']}
      overlayClassName={overlayClassName}
    >
      <Badge
        count={trackersToUse.length}
        size={'small'}
        offset={[-5, 1]}
        color={RacemapColors.PaleBlue}
      >
        <Icon disabled={disabled} />
      </Badge>
    </Dropdown>
  );
};

const Icon = styled(IconGear)<{ disabled: boolean }>`
  color: ${({ disabled }) => (disabled ? `${RacemapColors.LightGray} !important` : 'unset')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed !important' : 'unset')};
  height: 15px;
  padding-left: 8px;
  padding-right: 4px;
`;

const TrackersInfo = styled.div`
  color: black;
`;

const overlayClassName = css`
  .ant-dropdown-menu-submenu-title {
    align-items: center;
  }
`;
